import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';
import { useDatumStore } from '../../stores/datumStore';
import { useWidgetStore } from '../../stores/widgetStore';
import { Card } from '../../components/common/Card';
import { Avatar } from '../../components/common/Avatar';
import { Button } from '../../components/common/Button';
import { Loading } from '../../components/common/Loading';
import { Dashboard } from '../coach/Dashboard';
import { ROUTES } from '../../utils/constants';
import type { Datum } from '../../api/datum';
import { FormWidget } from '@/components/widgets/FormWidget';
import { getMyCohorts, getCoaches, getInbox } from '../../api/messaging';
import { listUsers } from '../../api/users';
import type { Cohort, InboxItem } from '../../types/messaging';
import { GenericWidget } from '@/components/widgets/GenericWidget';

interface HomeProps {
  tenant: string;
}

export function Home({ tenant }: HomeProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { config } = useTenantStore();
  const {
    loadDatumForUser,
    datum,
    datumLoading,
  } = useDatumStore();
  const {
    loadConfig,
    togglePin,
    isPinned,
    config: widgetConfig,
  } = useWidgetStore();

  const [showFabMenu, setShowFabMenu] = useState(false);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [showAnnounceModal, setShowAnnounceModal] = useState(false);
  const [coaches, setCoaches] = useState<Set<string>>(new Set());

  // Inbox state
  const [inboxMessages, setInboxMessages] = useState<InboxItem[]>([]);
  const [inboxLoading, setInboxLoading] = useState(false);

  // Collapsible sections
  const [isInboxCollapsed, setIsInboxCollapsed] = useState(false);
  const [isPostsCollapsed, setIsPostsCollapsed] = useState(false);

  const isCoach = user?.role === 'coach' || user?.role === 'admin';

  // Show Dashboard for coaches
  if (isCoach) {
    return <Dashboard tenant={tenant} />;
  }

  useEffect(() => {
    if (user) {
      loadDatumForUser(tenant);
      loadConfig(tenant);
      loadUserCohorts();
      loadCoaches();
      loadInbox();
    }
  }, [user, tenant]);

  const loadUserCohorts = async () => {
    try {
      const response = await getMyCohorts(tenant);
      setCohorts(response.cohorts);
    } catch (error) {
      console.error('Failed to load cohorts:', error);
    }
  };

  const loadCoaches = async () => {
    try {
      const response = await getCoaches(tenant);
      const coachIds = new Set(response.coachIds);
      setCoaches(coachIds);
    } catch (error) {
      console.error('Failed to load coaches:', error);
    }
  };

  const loadInbox = async () => {
    setInboxLoading(true);
    try {
      const response = await getInbox(tenant, { limit: 50 });
      setInboxMessages(response.items);
    } catch (error) {
      console.error('Failed to load inbox:', error);
    } finally {
      setInboxLoading(false);
    }
  };

  const handleMessageCoach = async () => {
    setShowFabMenu(false);
    try {
      const response = await listUsers(tenant, { role: 'coach', limit: 1 });
      if (response.users.length > 0) {
        const coach = response.users[0];
        navigate(`/${tenant}/chat/${coach.id}`);
      } else {
        alert('No coaches available to message');
      }
    } catch (error) {
      console.error('Failed to load coaches:', error);
      alert('Failed to find a coach to message');
    }
  };

  const handleAnnounce = () => {
    setShowFabMenu(false);
    setShowAnnounceModal(true);
  };

  // Categorize inbox messages
  const { privateMessages, groupMessages } = useMemo(() => {
    const privateMessages: InboxItem[] = [];
    const groupMessages: Map<string, { cohort: Cohort | undefined; messages: InboxItem[] }> = new Map();

    inboxMessages.forEach((msg) => {
      if (msg.target_kind === 'user') {
        // Private DM - check if from/to a coach
        if (coaches.has(msg.sender_id) || coaches.has(msg.target_id)) {
          privateMessages.push(msg);
        }
      } else if (msg.target_kind === 'cohort') {
        // Group message
        const existing = groupMessages.get(msg.target_id);
        if (existing) {
          existing.messages.push(msg);
        } else {
          const cohort = cohorts.find(c => c.id === msg.target_id);
          groupMessages.set(msg.target_id, { cohort, messages: [msg] });
        }
      }
    });

    return { privateMessages, groupMessages };
  }, [inboxMessages, coaches, cohorts]);

  // Sort datum: pinned first, then by order, then by creation date
  const sortedDatum = useMemo(() => {
    return [...datum].sort((a, b) => {
      const aPinned = isPinned(a.id);
      const bPinned = isPinned(b.id);

      if (aPinned !== bPinned) {
        return aPinned ? -1 : 1;
      }

      const aIndex = widgetConfig.order.indexOf(a.id);
      const bIndex = widgetConfig.order.indexOf(b.id);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return b.createdAt - a.createdAt;
    });
  }, [datum, widgetConfig, isPinned]);

  const handlePinToggle = async (signalId: string) => {
    try {
      await togglePin(tenant, signalId);
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
  };

  const renderWidget = (datum: Datum) => {
    const props = {
      key: datum.id,
      datumId: datum.id,
      tenant,
      isPinned: isPinned(datum.id),
      onPinToggle: () => handlePinToggle(datum.id),
    };

    switch (datum.kind) {
      case 'equation':
      case 'reflection':
      case 'checklist':
      case 'reminder':
      case 'form':
      case 'poll':
      case 'goal':
      case 'habit':
        return <FormWidget {...props} kind={datum.kind} />;
      default:
        return <GenericWidget {...props} />;
    }
  };

  // Get unread counts
  const unreadPrivateCount = privateMessages.filter(m => !m.is_read).length;
  const unreadGroupCount = Array.from(groupMessages.values())
    .reduce((sum, g) => sum + g.messages.filter(m => !m.is_read).length, 0);
  const totalUnread = unreadPrivateCount + unreadGroupCount;

  // Get first coach for DM navigation
  const firstCoachId = Array.from(coaches)[0];

  if (!user) return null;

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <Card>
        <div className="flex items-center gap-4">
          <Avatar src={user.avatarUrl} name={user.name} size="xl" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {user.name.split(' ')[0]}!
            </h1>
            <p className="text-gray-600">
              {config?.branding.tagline || 'Your coaching journey continues here.'}
            </p>
          </div>
        </div>
      </Card>

      {/* Inbox Section (Collapsible) */}
      <Card>
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setIsInboxCollapsed(!isInboxCollapsed)}
        >
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
            {totalUnread > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {totalUnread} new
              </span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isInboxCollapsed ? '' : 'rotate-180'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {!isInboxCollapsed && (
          <div className="mt-4 space-y-3">
            {inboxLoading ? (
              <Loading message="Loading messages..." />
            ) : (
              <>
                {/* Private Messages (Coach DMs) */}
                {firstCoachId && (
                  <button
                    onClick={() => navigate(`/${tenant}/chat/${firstCoachId}`)}
                    className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Coach</p>
                        <p className="text-sm text-gray-500">
                          {privateMessages.length > 0
                            ? `${privateMessages.length} message${privateMessages.length !== 1 ? 's' : ''}`
                            : 'No messages yet'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {unreadPrivateCount > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {unreadPrivateCount}
                        </span>
                      )}
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                )}

                {/* Group Messages */}
                {cohorts.map((cohort) => {
                  const groupData = groupMessages.get(cohort.id);
                  const messageCount = groupData?.messages.length || 0;
                  const unreadCount = groupData?.messages.filter(m => !m.is_read).length || 0;

                  return (
                    <button
                      key={cohort.id}
                      onClick={() => navigate(`/${tenant}/chat/${cohort.id}`)}
                      className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-900">{cohort.name}</p>
                          <p className="text-sm text-gray-500">
                            {messageCount > 0
                              ? `${messageCount} message${messageCount !== 1 ? 's' : ''}`
                              : 'No messages yet'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {unreadCount}
                          </span>
                        )}
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  );
                })}

                {cohorts.length === 0 && !firstCoachId && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No messages yet
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Card>

      {/* Posts Section (Collapsible) */}
      <Card>
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setIsPostsCollapsed(!isPostsCollapsed)}
        >
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900">Posts</h2>
            {sortedDatum.length > 0 && (
              <span className="text-sm text-gray-500">
                ({sortedDatum.length})
              </span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isPostsCollapsed ? '' : 'rotate-180'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {!isPostsCollapsed && (
          <div className="mt-4">
            {datumLoading ? (
              <Loading message="Loading posts..." />
            ) : sortedDatum.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <p className="mt-2">No posts yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sortedDatum.map((d) => renderWidget(d))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Complete Profile CTA */}
      <Card className="bg-primary-50 border-primary-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-primary-900">Complete Your Profile</h3>
            <p className="text-sm text-primary-700">
              Add more details to get personalized recommendations
            </p>
          </div>
          <Link to={ROUTES.profile(tenant)}>
            <Button variant="primary" size="sm">
              Update Profile
            </Button>
          </Link>
        </div>
      </Card>

      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-50">
        {showFabMenu && (
          <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl border border-gray-200 py-2 mb-2 min-w-[200px]">
            <button
              onClick={handleMessageCoach}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <span className="font-medium">Message Coach</span>
            </button>
            <button
              onClick={handleAnnounce}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
              <span className="font-medium">Announce</span>
            </button>
          </div>
        )}
        <button
          onClick={() => setShowFabMenu(!showFabMenu)}
          className="bg-primary-600 hover:bg-primary-700 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all"
        >
          <svg className={`w-6 h-6 transition-transform ${showFabMenu ? 'rotate-45' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Announce Modal */}
      {showAnnounceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Announce to Group</h2>
              <button onClick={() => setShowAnnounceModal(false)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">Select a group to send your announcement:</p>
              <div className="space-y-2">
                {cohorts.map((cohort) => (
                  <button
                    key={cohort.id}
                    onClick={() => {
                      setShowAnnounceModal(false);
                      navigate(`/${tenant}/chat/${cohort.id}`);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between group"
                  >
                    <span className="font-medium text-gray-900">{cohort.name}</span>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
              {cohorts.length === 0 && (
                <p className="text-center py-8 text-gray-500 text-sm">You are not a member of any groups yet.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
